import { CareEnvironment, CarePhase } from '../../../../appModel';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH, unlimitedMaxBenefitMagicNumber } from '../sharedConstants';
import { computeMonthlyCostMetrics } from '../sharedEligibility';
import {
  getCalendarPolicyYear,
  getCareCostAnchorYear,
  getDailyBenefitAmountForEnvironment,
  getFirstContributingPhaseStartYear,
  getPhaseCareMonthsByPhase,
  getMaxMonthlyBenefitAmount,
  getMonthlyBenefitAmountForEnvironment,
  growthFactor,
} from '../sharedPolicyConfig';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import {
  deriveIneligibilityReason,
  deriveIneligibleDayBreakdown,
} from '../deriveIneligibilityReason';
import { buildPrePolicyMonth } from '../buildPrePolicyMonth';
import { applyIndemnitySurplus } from '../sharedSurplus';

// ---------------------------------------------------------------------------
// CCAH / Continuing Care at Home MEMBERSHIP PROGRAMS — Fortified Life (Western
// Home) and Stay Your Way. Both carry `calculationType === longTermCareInsurance`
// but are routed specially via `isMembershipProgram()` (FundingPolicyTypeDef.ts).
//
// This is the closest sibling of `traditionalLtcStrategy` (single dollar pool,
// reimbursement, no second pool, no death benefit) with FOUR membership-specific
// differences (per the `isMembershipProgram` comment block and the legacy
// short-circuit at calcFundingSourceDerivatives.ts:827-835):
//
//   1. PAYOUT — single-pool reimbursement, like traditional LTC. No
//      continuation/EOB pool, NO death benefit (`finalizeDeathBenefit → 0`).
//   2. NO 2-ADL TRIGGER — care is benefit-eligible from day 1 regardless of ADL
//      count. We force `non2AdlDays = 0` (legacy returns `daysWhereLessThanTwo-
//      ADLs: 0`). KNOWN QUIRK / over-eligibility: the Fortified Life contract
//      actually gates Home Site Services on >= 1 ADL deficiency (§3.4), but we
//      have no 1-ADL gate primitive and legacy models 0-ADL, so we mirror legacy
//      (slight over-pay vs. the contract). Documented in field-mapping.md issue #2.
//   3. NO WAITING / ELIMINATION PERIOD — all care days eligible immediately. We
//      seed `waitingPeriod: { homeDays: 0, facilityDays: 0 }` unconditionally and
//      never decrement it (legacy returns `applicableWaitingPeriodDays: 0`). The
//      `in-wp` ineligibility reason can never fire.
//   4. PREMIUMS CONTINUE THROUGH CARE / extended premium-investment period —
//      this is a COST-side concern owned entirely by `calcPolicyTotalCost`
//      (calcFundingSourceDerivatives.ts:2762, keyed off `isMembershipProgram`).
//      It is OUTSIDE the claim-projection engine and is NOT modeled here.
//
// UNCAPPED POOL (DECISION RESOLVED 2026-06-03):
// - Membership programs are an UNCAPPED service obligation — the contract defines
//   no lifetime dollar pool (field-mapping.md issue #1). We model this exactly
//   like legacy `handleUnlimitedBenefit`: when `policyMaximumBenefitAmount` is
//   null/undefined, seed the base pool to `unlimitedMaxBenefitMagicNumber`
//   (999999999) and set the unlimited flag, so the pool NEVER binds. When the
//   membership also has no configured daily/monthly cap, payout is limited only
//   by eligible care cost and care duration. The `pool-exhausted` milestone
//   therefore never fires for a real (uncapped) membership program.
// - We do NOT copy `traditionalLtcStrategy.getAtIssuePool`'s `?? 0` fallback for
//   the null case — that would yield a $0 pool (all out-of-pocket), the opposite
//   of unlimited. A populated max (including 0) is honored verbatim as a real cap
//   (e.g. for tests / planner-supplied finite pools).
//
// INFLATION: membership programs have NO benefit-side inflation rider (services
// are provided in-kind at cost — there is nothing to inflate; field-mapping.md
// "Inflation protection"). With `policyInflationProtection = 0` the inflation
// branch below is harmless dead code. It is retained mirroring traditional LTC in
// case a planner enters a non-zero rate; bit-equal parity only holds at 0%.
//
// Like traditional LTC, neither a per-claim nor a per-month carry-forward is
// needed beyond the universal slots, so both state and deltas shapes are empty.
// ---------------------------------------------------------------------------

export interface MembershipProgramState {
  // Indemnity-surplus reserve. Indemnity policies pay a fixed monthly benefit
  // regardless of cost; the excess above eligible cost is banked here and
  // drained on later shortfall months (legacy `applyIndemnityPayoutToPhase`
  // parity). Always 0 on reimbursement-election policies (their payout never
  // exceeds eligible cost), so the surplus model is a numeric no-op there.
  indemnitySurplus: number;
}
export interface MembershipProgramDeltas {
  indemnitySurplusAccrued: number;
  indemnitySurplusApplied: number;
}

interface InflationConfig {
  rate: number;
  capYears: number;
  isSimple: boolean;
}

function readInflation(ltcPolicy: PolicyFundingSource): InflationConfig {
  const capYearsRaw = ltcPolicy.policyInflationProtectionYears;
  return {
    rate: ltcPolicy.policyInflationProtection ?? 0,
    capYears: capYearsRaw && capYearsRaw > 0 ? capYearsRaw : 0,
    isSimple: ltcPolicy.isSimpleInflationProtection === true,
  };
}

// A membership program with a null/undefined `policyMaximumBenefitAmount` is
// UNCAPPED, mirroring legacy `isUnlimitedBenefit` (a missing max on a non-hybrid
// policy returns `unlimitedMaxBenefitMagicNumber` and flags
// `policyBenefitIsUnlimited`). A populated max (including 0) is a real cap.
function isUnlimitedPool(ltcPolicy: PolicyFundingSource): boolean {
  return ltcPolicy.policyMaximumBenefitAmount == null;
}

// At-issue lifetime dollar pool. Uncapped programs seed the sentinel so payouts
// are never pool-limited (the client renders any pool >= the sentinel as
// "Unlimited"). A populated max (including 0) is used verbatim — explicitly NOT
// the `?? 0` fallback (which would collapse the uncapped product to $0).
function getAtIssuePool(ltcPolicy: PolicyFundingSource): number {
  if (isUnlimitedPool(ltcPolicy)) return unlimitedMaxBenefitMagicNumber;
  return ltcPolicy.policyMaximumBenefitAmount ?? 0;
}

function hasConfiguredMonthlyCap(ltcPolicy: PolicyFundingSource): boolean {
  return [
    ltcPolicy.dailyHomeBenefitAmount,
    ltcPolicy.dailyAssistedLivingBenefitAmount,
    ltcPolicy.dailyNursingHomeBenefitAmount,
    ltcPolicy.dailyIndependentLivingBenefitAmount,
  ].some(value => value != null);
}

export const membershipProgramStrategy: PolicyProjectionStrategy<
  MembershipProgramState,
  MembershipProgramDeltas
> = {
  initClaimState(client, ltcPolicy): MonthlyState<MembershipProgramState> {
    const atIssuePool = getAtIssuePool(ltcPolicy);
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const inflation = readInflation(ltcPolicy);
    // Pre-inflate the pool to its value at claim start (anniversaries between
    // issue and claim start are not iterated). Uncapped pools stay flat at the
    // sentinel — no pre-claim inflation, no utilized-to-date reduction (legacy's
    // unlimited path returns the magic number verbatim).
    const claimStartPolicyYear = getCalendarPolicyYear(client as Client, 0);
    const poolAtClaimStart = isUnlimitedPool(ltcPolicy)
      ? atIssuePool
      : atIssuePool *
        growthFactor(inflation.rate, inflation.capYears, inflation.isSimple, claimStartPolicyYear);
    return {
      pools: {
        baseBenefitPool: isUnlimitedPool(ltcPolicy)
          ? atIssuePool
          : Math.max(poolAtClaimStart - utilizedToDate, 0),
        // No continuation pool and no death benefit on a membership program.
        extensionOfBenefitsPool: 0,
        deathBenefit: 0,
      },
      // NO waiting period on a membership program — seed both buckets to 0
      // unconditionally (do NOT read homeCareWaitingPeriodDaysRemaining etc.).
      // The buckets never decrement, so the `in-wp` reason can never fire.
      waitingPeriod: { homeDays: 0, facilityDays: 0 },
      strategyState: { indemnitySurplus: 0 },
    };
  },

  buildClaimTerms(client, ltcPolicy): ClaimTerms {
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const dailyBenefit = baseMonthly / DAYS_IN_MONTH;
    const inflation = readInflation(ltcPolicy);
    const clientAsClient = client as Client;
    const projectionAnchorYear = getCareCostAnchorYear(clientAsClient);
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    // Prior utilization deducted from the pool at claim start, capped at the
    // pre-inflated pool (mirrors `initClaimState`). Uncapped (sentinel) pools
    // deduct nothing — the legacy unlimited path never reduces by utilization.
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const claimStartPolicyYear = getCalendarPolicyYear(clientAsClient, 0);
    const poolAtClaimStart =
      getAtIssuePool(ltcPolicy) *
      growthFactor(inflation.rate, inflation.capYears, inflation.isSimple, claimStartPolicyYear);
    const priorUtilization = isUnlimitedPool(ltcPolicy)
      ? 0
      : Math.min(utilizedToDate, poolAtClaimStart);
    return {
      strategyId: 'membership-program',
      priorUtilization,
      nominalDailyBenefit: dailyBenefit,
      nominalDailyBenefitBase: dailyBenefit,
      // Same sparse-field fallback the payout cap uses, so the labelled
      // per-environment benefits always match what the engine actually pays.
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.home,
        ),
        [CareEnvironment.assistedLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.assistedLiving,
        ),
        [CareEnvironment.fullCareFacility]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.fullCareFacility,
        ),
        [CareEnvironment.independentLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.independentLiving,
        ),
      },
      inflationRate: inflation.rate,
      inflationCapYears: inflation.capYears,
      inflationIsSimple: inflation.isSimple,
      baseBenefitPoolInflationRate: inflation.rate,
      baseBenefitPoolInflationCapYears: inflation.capYears,
      baseBenefitPoolAtIssue: getAtIssuePool(ltcPolicy),
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      phaseCareMonthsByPhase: getPhaseCareMonthsByPhase(clientAsClient),
      firstPhaseStartYear: getFirstContributingPhaseStartYear(clientAsClient),
      policyPremiumStartYear: ltcPolicy.policyPremiumStartYear ?? projectionAnchorYear,
      // No death benefit on a membership program.
      minimumGuaranteedDeathBenefit: 0,
      isReimbursement: !isIndemnity,
      isIndemnity,
      isUnlimited: isUnlimitedPool(ltcPolicy),
      // Real membership contracts define no daily/monthly cap — the engine
      // then sets each row's effective cap to its eligible cost, so the UI
      // needs this signal to avoid presenting that echo as a policy cap.
      hasConfiguredBenefitCap: hasConfiguredMonthlyCap(ltcPolicy),
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    const { ltcPolicy, policyYear } = input;
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const inflation = readInflation(ltcPolicy);
    const factor = growthFactor(
      inflation.rate,
      inflation.capYears,
      inflation.isSimple,
      policyYear,
    );

    const atIssuePool = getAtIssuePool(ltcPolicy);
    const inflatedMonthlyCap = baseMonthly * factor;
    // Uncapped pools stay flat at the sentinel (client renders >= sentinel as
    // "Unlimited"); only the daily/monthly cap inflates.
    const inflatedPool = isUnlimitedPool(ltcPolicy) ? atIssuePool : atIssuePool * factor;

    return {
      policyYear,
      effectiveDailyBenefit: inflatedMonthlyCap / DAYS_IN_MONTH,
      effectiveDailyBenefitBase: inflatedMonthlyCap / DAYS_IN_MONTH,
      effectiveDailyBenefitRider: 0,
      // No death benefit / continuation pool on a membership program.
      effectiveFaceAmount: 0,
      effectiveContinuationBenefit: 0,
      effectiveTotalLtcBenefit: inflatedPool,
      monthlyBenefitCap: inflatedMonthlyCap,
      theoreticalLtcPoolCeiling: inflatedPool,
    };
  },

  computeMonth(
    input: MonthComputeInput<MembershipProgramState, MembershipProgramDeltas>,
  ): MonthlyEntry<MembershipProgramState, MembershipProgramDeltas> {
    const { client, ltcPolicy, phaseCarePlan, monthlyCareInput, openState, priorMonth } = input;
    const { monthIndex, carePhase, monthsIntoPhase } = monthlyCareInput;
    const clientAsClient = client as Client;

    const costMetrics = computeMonthlyCostMetrics(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );
    const claimYearOffset = Math.floor(monthIndex / 12);
    const policyYear = getCalendarPolicyYear(clientAsClient, monthIndex);

    const context: MonthContext = {
      monthIndex,
      policyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, policyYear),
      claimYearOffset,
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
    };

    // Pre-policy gate. Single-life floors its policy year at 1, so this is
    // defensive — a care month before the premium-start year pays $0, full OOP.
    if (policyYear < 1) {
      const prePolicyFacts: MonthlyCareFacts = {
        nominalCareCost: costMetrics.nominalCareCost,
        effectiveCareCost: costMetrics.effectiveCareCost,
        dailyCareCost: costMetrics.dailyCareCost,
        careCostInflationYears: costMetrics.careCostInflationYears,
        projectedCareYear: costMetrics.projectedCareYear,
        non2AdlDays: 0,
        coverageEligibleDays: 0,
        waitingPeriodDaysConsumedHome: 0,
        waitingPeriodDaysConsumedFacility: 0,
        appliedMonthlyBenefitCap: 0,
        appliedCapPoolSource: 'base',
      };
      return buildPrePolicyMonth<MembershipProgramState, MembershipProgramDeltas>({
        context,
        facts: prePolicyFacts,
        openState,
        strategyDeltas: { indemnitySurplusAccrued: 0, indemnitySurplusApplied: 0 },
      });
    }

    // 1. Day classification — NO 2-ADL gate, NO waiting period. All 30 days are
    //    eligible from day 1 (legacy short-circuit at :827-835). The WP state
    //    stays at 0 and is never consumed.
    const non2AdlDays = 0;
    const eligibleDays = DAYS_IN_MONTH;
    const waitingPeriodDaysConsumedHome = 0;
    const waitingPeriodDaysConsumedFacility = 0;
    const closeHomeWp = openState.waitingPeriod.homeDays;
    const closeFacilityWp = openState.waitingPeriod.facilityDays;
    // Always satisfied — there is no elimination period to serve.
    const closeRelevantWp = 0;

    // 2. Per-month dollar cap (this year's inflation factor; flat at 0%).
    //    Fortified/Stay Your Way contracts do not define a daily/monthly cap, and
    //    CCRC-created rows omit all daily benefit fields. In that no-cap shape,
    //    mirror legacy's null-daily path by letting eligible cost set the month's
    //    effective cap instead of collapsing payout to $0.
    const inflation = readInflation(ltcPolicy);
    const eligibleCost = costMetrics.dailyCareCost * eligibleDays;
    const hasMonthlyCap = hasConfiguredMonthlyCap(ltcPolicy);
    const configuredMonthlyCap =
      getMonthlyBenefitAmountForEnvironment(ltcPolicy, phaseCarePlan.appliedCareEnvironment) *
      growthFactor(inflation.rate, inflation.capYears, inflation.isSimple, policyYear);
    const monthlyCap = hasMonthlyCap ? configuredMonthlyCap : eligibleCost;

    // 3. Annual anniversary pool accrual on the REMAINING balance (compound) /
    //    constant step (simple). Membership programs have no benefit inflation
    //    rider in practice (rate is 0) so this is dead code at parity; retained
    //    mirroring traditional LTC. Skipped on the uncapped sentinel pool.
    let inflationAccrual = 0;
    if (priorMonth && policyYear !== priorMonth.context.policyYear) {
      // A capYears-year rider applies capYears increases, the last one landing
      // on the anniversary entering policy year capYears + 1 — matching
      // `growthFactor`'s min(policyYear − 1, capYears) step convention, so the
      // in-claim pool agrees with `buildYearSnapshot` through the final cap year.
      const withinCap =
        inflation.capYears <= 0 || policyYear <= inflation.capYears + 1;
      if (
        !isUnlimitedPool(ltcPolicy) &&
        openState.pools.baseBenefitPool > 0 &&
        withinCap &&
        inflation.rate > 0
      ) {
        inflationAccrual = inflation.isSimple
          ? getAtIssuePool(ltcPolicy) * inflation.rate
          : openState.pools.baseBenefitPool * inflation.rate;
      }
    }
    const poolAfterAccrual = openState.pools.baseBenefitPool + inflationAccrual;

    // 4. Payout: reimbursement (default) or cash indemnity. Single pool only.
    //    Configured caps are pro-rated by eligible days. A missing cap means the
    //    eligible cost is already the whole row limit, so do not pro-rate it again.
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    const proRatedCap = hasMonthlyCap ? monthlyCap * (eligibleDays / DAYS_IN_MONTH) : monthlyCap;

    let drawFromPool = 0;
    let indemnityPaid = 0;
    if (isIndemnity && poolAfterAccrual > 0 && eligibleDays > 0) {
      const indemnityAmount = Math.min(proRatedCap, poolAfterAccrual);
      drawFromPool = indemnityAmount;
      indemnityPaid = indemnityAmount;
    } else {
      const wouldPay = Math.min(eligibleCost, proRatedCap);
      drawFromPool = Math.min(wouldPay, poolAfterAccrual);
    }
    // Gross cash this row pays out. On indemnity this is the capped cash
    // regardless of cost; on reimbursement it is the reimbursed amount
    // (≤ eligible cost). The pool depletes by this gross cash; the surplus
    // model is a separate accounting reserve layered on top.
    const rowPayout = drawFromPool;

    // 4b. Indemnity-surplus accounting (per-row, unconditional). Cost-coverage
    //     is capped at eligible cost; cash above eligible cost banks as surplus
    //     and drains on later shortfall rows. On reimbursement-election
    //     policies the open surplus is always 0 and the reimbursed payout never
    //     exceeds eligible cost, so this is a numeric no-op (no accrual/drain).
    const surplusResult = applyIndemnitySurplus({
      openSurplus: openState.strategyState.indemnitySurplus,
      indemnityPayout: rowPayout,
      eligibleCost,
      fullCost: costMetrics.effectiveCareCost,
      // Single pool: exhausted when the post-accrual balance can't fund a dollar.
      policyExhausted: poolAfterAccrual <= 0,
    });

    // 5. Closing balance. No death benefit to deplete.
    const closePool = poolAfterAccrual - drawFromPool;

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      careCostInflationYears: costMetrics.careCostInflationYears,
      projectedCareYear: costMetrics.projectedCareYear,
      non2AdlDays,
      coverageEligibleDays: eligibleDays,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      appliedMonthlyBenefitCap: monthlyCap,
      appliedCapPoolSource: 'base',
    };

    const payout: PayoutSummary = {
      ltcCoveragePaid: surplusResult.ltcCoveragePaid,
      ltcPayoutToCost: surplusResult.ltcPayoutToCost,
      ineligibleCost: costMetrics.effectiveCareCost - surplusResult.ltcPayoutToCost,
      indemnityPayout: indemnityPaid,
    };

    const deltas: MonthlyDeltas<MembershipProgramDeltas> = {
      payout,
      base: { depletion: drawFromPool, inflationAccrual },
      // No second pool — EOB flow is always zero.
      eob: { depletion: 0, inflationAccrual: 0 },
      // No death benefit on a membership program.
      deathBenefit: { reduction: 0, flooredAtMinimum: false },
      strategyDeltas: {
        indemnitySurplusAccrued: surplusResult.surplusAccrued,
        indemnitySurplusApplied: surplusResult.surplusApplied,
      },
    };

    const closeState: MonthlyState<MembershipProgramState> = {
      pools: {
        baseBenefitPool: closePool,
        extensionOfBenefitsPool: 0,
        deathBenefit: 0,
      },
      waitingPeriod: { homeDays: closeHomeWp, facilityDays: closeFacilityWp },
      strategyState: { indemnitySurplus: surplusResult.closeSurplus },
    };

    const status: MonthlyStatus = {
      ltcPoolExhausted: closePool <= 0,
      clientPoolExhausted: closePool <= 0,
      // No elimination period — always satisfied.
      epSatisfied: closeRelevantWp === 0,
    };

    const milestones = deriveStandardMilestones<MembershipProgramState, MembershipProgramDeltas>({
      context,
      facts,
      openState,
      closeState,
      deltas,
      priorMonth,
      retroactiveLumpPaid: 0,
      minimumGuaranteedDeathBenefit: 0,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      hasInflationProtection: inflation.rate > 0,
    });

    const ineligibilityInput = {
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState,
      closeState,
      payout,
    };
    const ineligibilityReason =
      deriveIneligibilityReason<MembershipProgramState>(ineligibilityInput);
    const ineligibleDayBreakdown =
      deriveIneligibleDayBreakdown<MembershipProgramState>(ineligibilityInput);

    return {
      context,
      facts,
      openState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason, ineligibleDayBreakdown },
    };
  },

  // Membership programs have no death benefit — always 0.
  finalizeDeathBenefit(_input: FinalizeDeathBenefitInput<MembershipProgramState>): number {
    return 0;
  },
};
